import { useState, useMemo, useEffect } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from '@shared/core';
import type { EmailInputFields, EmailType, EmailValidationMessages } from '@shared/components/EmailsAndEcards/Emails.types';
import { getFormikValidationSchema } from '@shared/components/EmailsAndEcards/Emails.utils';
import { useEcardsTelemetry } from '@apps/ecard/Ecard.telemetry';
import { usePostOfficeBottomSheetState } from '@apps/postOffice/PostOffice.bottomSheet';
import type { EcardType } from '@graphql/generated';
import { MESSAGE_MIN_CHARACTER_LIMIT } from '@shared/constants/messaging';
interface UseEmailEditorControllerArgs
  extends Readonly<{
    emailInput: EmailInputFields;
    emailType: EmailType;
    updateEmailInput?: (emailInput: EmailInputFields) => void;
    isMessageLoading?: boolean;
    editEmailContentTrack?: (ecardType: EcardType) => void;
    ecardDraftType?: EcardType;
  }> {}

export const useEmailEditorController = (args: UseEmailEditorControllerArgs) => {
  const { emailInput, emailType, updateEmailInput, isMessageLoading, editEmailContentTrack, ecardDraftType } = args;
  const { editEmailTrack, ...actions } = useEcardsTelemetry();
  const { setParentViewState } = usePostOfficeBottomSheetState();

  const { tq } = useTranslation('emailsAndEcards');

  // Memoize validation messages with tq dependency to ensure messages update on locale change.
  // When locale changes: tq changes → validationMessages recomputes → formikValidationSchema recomputes.
  const validationMessages: EmailValidationMessages = useMemo(
    () => ({
      emailSubjectErrorMessage: tq`Email Subject is required`,
      replyToErrorMessage: tq`Reply-to Email is required`,
      buttonTextError: tq`Button Text is required`,
      messageMinLengthError: tq`Your message needs more detail so it ends up in your guests' inbox. Please include at least ${MESSAGE_MIN_CHARACTER_LIMIT} characters. Spam filters flag both short and generic content.`,
      invalidSubjectIsEmailOrUrlError: tq`Sorry! To keep our emails secure, email addresses and URLs are not allowed in the subject of the email.`,
      invalidHeadlineIsEmailOrUrlError: tq`Sorry! To keep our emails secure, email addresses and URLs are not allowed in the headline of the email.`,
      invalidSalutationIsEmailOrUrlError: tq`Sorry! To keep our emails secure, email addresses and URLs are not allowed in the salutation of the email.`,
      invalidSubheadlineIsEmailOrUrlError: tq`Sorry! To keep our emails secure, email addresses and URLs are not allowed in the subheadline of the email.`,
      invalidMessageIsEmailOrUrlError: tq`Sorry! To keep our emails secure, email addresses and URLs are not allowed in the message of the email.`,
      invalidButtonTextIsEmailOrUrlError: tq`Sorry! To keep our emails secure, email addresses and URLs are not allowed in the button text of the email.`,
    }),
    [tq],
  );

  // Recomputes when emailType or validationMessages change (validationMessages changes on locale switch via tq dependency above)
  const formikValidationSchema = useMemo(() => getFormikValidationSchema(emailType, validationMessages), [emailType, validationMessages]);

  const emailFormik = useFormik<EmailInputFields>({
    initialValues: emailInput,
    validationSchema: formikValidationSchema,
    enableReinitialize: true,
    onSubmit: values => {
      if (updateEmailInput) {
        updateEmailInput(values);
      }
    },
  });

  const [isEmailEditPanelShown, setIsEmailEditPanelShown] = useState<boolean>(false);
  const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] = useState<boolean>(false);

  const disableEditEmail = emailType === 'thankyou' && isMessageLoading;

  const handleToggleEmailEditPanel = () => {
    if (!disableEditEmail) {
      actions.launchEmailEditor();

      if (!isEmailEditPanelShown && ecardDraftType) {
        editEmailContentTrack?.(ecardDraftType);
      }

      setIsEmailEditPanelShown(!isEmailEditPanelShown);
    }
  };

  useEffect(() => {
    setParentViewState(prev => ({ ...prev, openEmailEditor: handleToggleEmailEditPanel, disableEditEmail }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableEditEmail]);

  const onCancel = () => {
    actions.editEmailCancelClick();
    emailFormik.resetForm();
    setIsEmailEditPanelShown(false);
  };

  const handleCancel = () => {
    if (emailFormik.dirty) {
      setIsUnsavedChangesDialogOpen(true);
    } else {
      onCancel();
    }
  };

  const handleUnsavedChangesDialogClose = () => {
    setIsUnsavedChangesDialogOpen(false);
  };

  const handleUnsavedChangesDialogConfirm = () => {
    handleUnsavedChangesDialogClose();
    onCancel();
  };

  useEffect(() => {
    editEmailTrack.enter(ecardDraftType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    handleToggleEmailEditPanel,
    handleCancel,
    isEmailEditPanelShown,
    emailFormik,
    isUnsavedChangesDialogOpen,
    handleUnsavedChangesDialogClose,
    handleUnsavedChangesDialogConfirm,
  };
};
